import { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Typography,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    InventoryReminderScheduleConfig,
    ScheduleType,
    WeekDay,
    OccurrenceType,
} from '../../../types/inventoryReminder';
import DayOfMonthPicker from './DayOfMonthPicker';
import WeekdayPicker from './WeekdayPicker';
import RelativeDayPicker from './RelativeDayPicker';

interface InventoryReminderSettingsProps {
    config: InventoryReminderScheduleConfig | null;
    onChange: (config: InventoryReminderScheduleConfig | null) => void;
    disabled?: boolean;
}

type MonthlyMode = 'days' | 'relative';

const InventoryReminderSettings = ({
    config,
    onChange,
    disabled = false,
}: InventoryReminderSettingsProps) => {
    const { t } = useTranslation();

    const [enabled, setEnabled] = useState(config !== null);
    const [scheduleType, setScheduleType] = useState<ScheduleType>(
        config?.type || 'monthly'
    );
    const [monthlyMode, setMonthlyMode] = useState<MonthlyMode>(
        config?.occurrence ? 'relative' : 'days'
    );
    const [selectedDaysOfMonth, setSelectedDaysOfMonth] = useState<number[]>(
        config?.daysOfMonth || []
    );
    const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<WeekDay[]>(
        config?.daysOfWeek || []
    );
    const [occurrence, setOccurrence] = useState<OccurrenceType | undefined>(
        config?.occurrence
    );
    const [dayOfWeek, setDayOfWeek] = useState<WeekDay | undefined>(
        config?.dayOfWeek
    );

    useEffect(() => {
        if (!enabled) {
            onChange(null);
            return;
        }

        let newConfig: InventoryReminderScheduleConfig;

        if (scheduleType === 'weekly') {
            newConfig = {
                type: 'weekly',
                daysOfWeek: selectedDaysOfWeek,
            };
        } else {
            if (monthlyMode === 'relative' && occurrence && dayOfWeek) {
                newConfig = {
                    type: 'monthly',
                    occurrence,
                    dayOfWeek,
                };
            } else {
                newConfig = {
                    type: 'monthly',
                    daysOfMonth: selectedDaysOfMonth,
                };
            }
        }

        onChange(newConfig);
    }, [
        enabled,
        scheduleType,
        monthlyMode,
        selectedDaysOfMonth,
        selectedDaysOfWeek,
        occurrence,
        dayOfWeek,
    ]);

    const handleEnabledChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setEnabled(event.target.value === 'on');
    };

    const handleScheduleTypeChange = (event: SelectChangeEvent) => {
        setScheduleType(event.target.value as ScheduleType);
    };

    const handleMonthlyModeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setMonthlyMode(event.target.value as MonthlyMode);
    };

    const getPreviewText = (): string => {
        if (!enabled) {
            return t('settings.inventoryReminder.preview.disabled');
        }

        if (scheduleType === 'weekly') {
            if (selectedDaysOfWeek.length === 0) {
                return t(
                    'settings.inventoryReminder.validation.noDaysSelected'
                );
            }
            const dayNames = selectedDaysOfWeek
                .map((day) => t(`settings.inventoryReminder.weekday.${day}`))
                .join(', ');
            return t('settings.inventoryReminder.preview.weekly', {
                days: dayNames,
            });
        }

        if (monthlyMode === 'relative') {
            if (!occurrence || !dayOfWeek) {
                return t(
                    'settings.inventoryReminder.validation.noDaysSelected'
                );
            }
            return t('settings.inventoryReminder.preview.relative', {
                occurrence: t(
                    `settings.inventoryReminder.occurrence.${occurrence}`
                ).toLowerCase(),
                day: t(
                    `settings.inventoryReminder.weekday.${dayOfWeek}`
                ).toLowerCase(),
            });
        }

        if (selectedDaysOfMonth.length === 0) {
            return t('settings.inventoryReminder.validation.noDaysSelected');
        }
        const dayList = selectedDaysOfMonth.join(', ');
        return t('settings.inventoryReminder.preview.monthly', {
            days: dayList,
        });
    };

    const hasValidationError = (): boolean => {
        if (!enabled) return false;

        if (scheduleType === 'weekly') {
            return selectedDaysOfWeek.length === 0;
        }

        if (monthlyMode === 'relative') {
            return !occurrence || !dayOfWeek;
        }

        return selectedDaysOfMonth.length === 0;
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {t('settings.inventoryReminder.title')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                    >
                        {t('settings.inventoryReminder.enable.label')}
                    </Typography>
                    <RadioGroup
                        value={enabled ? 'on' : 'off'}
                        onChange={handleEnabledChange}
                    >
                        <FormControlLabel
                            value="off"
                            control={<Radio disabled={disabled} />}
                            label={t('settings.inventoryReminder.enable.off')}
                        />
                        <FormControlLabel
                            value="on"
                            control={<Radio disabled={disabled} />}
                            label={t('settings.inventoryReminder.enable.on')}
                        />
                    </RadioGroup>
                </Box>

                {enabled && (
                    <Box
                        sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                        }}
                    >
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>
                                {t(
                                    'settings.inventoryReminder.scheduleType.label'
                                )}
                            </InputLabel>
                            <Select
                                value={scheduleType}
                                onChange={handleScheduleTypeChange}
                                label={t(
                                    'settings.inventoryReminder.scheduleType.label'
                                )}
                                disabled={disabled}
                            >
                                <MenuItem value="weekly">
                                    {t(
                                        'settings.inventoryReminder.scheduleType.weekly'
                                    )}
                                </MenuItem>
                                <MenuItem value="monthly">
                                    {t(
                                        'settings.inventoryReminder.scheduleType.monthly'
                                    )}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {scheduleType === 'weekly' && (
                            <WeekdayPicker
                                selectedDays={selectedDaysOfWeek}
                                onChange={setSelectedDaysOfWeek}
                                disabled={disabled}
                            />
                        )}

                        {scheduleType === 'monthly' && (
                            <Box>
                                <RadioGroup
                                    value={monthlyMode}
                                    onChange={handleMonthlyModeChange}
                                    sx={{ mb: 2 }}
                                >
                                    <FormControlLabel
                                        value="days"
                                        control={<Radio disabled={disabled} />}
                                        label={t(
                                            'settings.inventoryReminder.scheduleType.monthly'
                                        )}
                                    />
                                    <FormControlLabel
                                        value="relative"
                                        control={<Radio disabled={disabled} />}
                                        label={t(
                                            'settings.inventoryReminder.scheduleType.relative'
                                        )}
                                    />
                                </RadioGroup>

                                {monthlyMode === 'days' && (
                                    <DayOfMonthPicker
                                        selectedDays={selectedDaysOfMonth}
                                        onChange={setSelectedDaysOfMonth}
                                        disabled={disabled}
                                    />
                                )}

                                {monthlyMode === 'relative' && (
                                    <RelativeDayPicker
                                        occurrence={occurrence}
                                        dayOfWeek={dayOfWeek}
                                        onOccurrenceChange={setOccurrence}
                                        onDayOfWeekChange={setDayOfWeek}
                                        disabled={disabled}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                <Alert
                    severity={hasValidationError() ? 'warning' : 'info'}
                    sx={{ mt: 2 }}
                >
                    {getPreviewText()}
                </Alert>
            </CardContent>
        </Card>
    );
};

export default InventoryReminderSettings;
